import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type {
  GetClientMailLanguageParams,
  GetClientMailLanguageResult,
} from '@/integrations/supabase/rpc-types';
import {
  APP_LANGUAGES,
  DEFAULT_LANGUAGE,
  LANGUAGE_LABELS,
  type AppLanguage,
} from '@/constants/languages';

/**
 * Lingua sul profilo del cliente (`users.preferred_language`), per email.
 *
 * `null` solo quando l'indirizzo non ha un profilo: la colonna ha un default,
 * quindi per chi e' anagrafato c'e' sempre un valore. Chi chiama ripiega su
 * `MAIL_DEFAULT_LANGUAGE`.
 *
 * `lookupFailed` distingue "non ha un account" da "non sono riuscito a
 * leggerlo": trattare un errore della RPC come assenza di account faceva dire
 * alla UI che un cliente esistente non aveva un profilo.
 */

// Le lingue del prodotto stanno in un posto solo: qui si riesportano con i nomi
// che usa il giro delle mail.
export type MailLanguage = AppLanguage;
export const MAIL_LANGUAGES = APP_LANGUAGES;
export const MAIL_DEFAULT_LANGUAGE = DEFAULT_LANGUAGE;
export const MAIL_LANGUAGE_LABELS = LANGUAGE_LABELS;

/**
 * La RPC entra in types.ts solo alla prossima rigenerazione: la si tipizza qui
 * invece di passare per `any`.
 */
type GetClientMailLanguageRpc = (
  name: 'get_client_mail_language',
  args: GetClientMailLanguageParams,
) => Promise<{ data: GetClientMailLanguageResult; error: { message: string } | null }>;

export const useClientMailLanguage = (email: string | null | undefined) => {
  const [profileLanguage, setProfileLanguage] = useState<MailLanguage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLookupFailed(false);
      if (!email) {
        setProfileLanguage(null);
        return;
      }

      setIsLoading(true);
      try {
        // RPC SECURITY DEFINER: il TD non puo' leggere users per un traveler
        // senza travel request assegnate.
        // bind: supabase.rpc usa `this`, staccata dal client lancia un TypeError
        // prima ancora di fare la richiesta.
        const rpc = supabase.rpc.bind(supabase) as unknown as GetClientMailLanguageRpc;
        const { data, error } = await rpc('get_client_mail_language', {
          p_email: email.toLowerCase().trim(),
        });

        if (error) throw error;
        const lang = typeof data === 'string' ? (data as MailLanguage) : null;
        setProfileLanguage(lang && MAIL_LANGUAGES.includes(lang) ? lang : null);
      } catch (err) {
        // Una lingua non leggibile non deve impedire l'invio: vale il ripiego e
        // chi invia puo' comunque scegliere la lingua a mano.
        console.error('Error reading client mail language:', err);
        setProfileLanguage(null);
        setLookupFailed(true);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [email]);

  return { profileLanguage, isLoading, lookupFailed };
};
